import React from 'react';
import PolicyAreaMultiSelect from './PolicyAreaMultiSelect';
import RegulatoryReferenceMultiSelect from './RegulatoryReferenceMultiSelect';
import DocumentTypeMultiSelect from './DocumentTypeMultiSelect';
import ScopeToggle from './ScopeToggle';

/**
 * The Start Page's search widget (GitHub issue #173).
 *
 * THE LAYOUT IS THE ASK: "have the search line take a whole line, with the
 * filters just below". So the input is its own full-width row and the filters
 * sit under it in the same auto-fit grid DocumentsPage uses -- deliberately not
 * that page's single grid, where Search is one cell among six.
 *
 * NO STATUS CONTROL, ANYWHERE. This page is ACTIVE-only throughout, which the
 * backend enforces in _start_page_query and this says in a line of muted text
 * rather than offering a picker that could only ever hold one value.
 *
 * "Documents", not "policies", in the user-visible strings -- issue #172's
 * convention; see pages/StartPage.js for why it applies to this screen.
 *
 * The mode toggle is two pill buttons in ScopeToggle's shape rather than a new
 * segmented-control primitive: this repo has no such component, and
 * btn-primary/btn-secondary already read as selected/not-selected everywhere
 * else in the app.
 */
function PolicySearchPanel({
  question, onQuestionChange,
  mode, onModeChange, semanticAvailable,
  scope, onScopeChange, hasHomeScope,
  policyAreaIds, onPolicyAreaChange,
  regulatoryReferenceIds, onRegulatoryReferenceChange,
  documentTypeIds, onDocumentTypeChange,
  onSearch, searching,
}) {
  const handleKeyDown = (e) => {
    // Enter searches. A single-line box on a landing page is the one place
    // where making somebody reach for a button is pure friction.
    if (e.key === 'Enter') onSearch();
  };

  return (
    <div className="card">
      <div className="form-group">
        <label>{mode === 'semantic' ? 'Ask a question' : 'Search'}</label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            style={{ flex: 1 }}
            placeholder={mode === 'semantic'
              ? 'e.g. If I work at Memorial Hospital, how much notice do I give for leave?'
              : 'Search active documents by title or content...'}
            value={question}
            onChange={(e) => onQuestionChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button className="btn btn-primary" onClick={onSearch} disabled={searching}>
            {searching ? '…' : '🔍 Search'}
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px',
      }}>
        <div className="form-group">
          <label>Mode</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className={`btn btn-small ${mode === 'lexical' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => onModeChange('lexical')}
            >
              🔤 Keywords
            </button>
            {/* Disabled WITH A REASON rather than hidden. A missing control is
                indistinguishable from a bug, and the backend would answer this
                request with word matches anyway -- so saying so up front is
                the honest version of the same outcome. */}
            <button
              type="button"
              className={`btn btn-small ${mode === 'semantic' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => onModeChange('semantic')}
              disabled={!semanticAvailable}
              title={semanticAvailable
                ? 'Ask in plain language and get an answer quoting the policies'
                : 'Answering questions is not switched on in this environment'}
            >
              🤖 Ask a question
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Policy Areas</label>
          <PolicyAreaMultiSelect
            selectedIds={policyAreaIds}
            onChange={onPolicyAreaChange}
          />
        </div>

        <div className="form-group">
          <label>Document Types</label>
          <DocumentTypeMultiSelect
            selectedIds={documentTypeIds}
            onChange={onDocumentTypeChange}
          />
        </div>

        <div className="form-group">
          <label>Regulatory References</label>
          <RegulatoryReferenceMultiSelect
            selectedIds={regulatoryReferenceIds}
            onChange={onRegulatoryReferenceChange}
          />
        </div>

        <div className="form-group">
          <label>Locations</label>
          {/* Applies immediately on click, like DocumentsPage's -- a two-state
              preference needs no confirm step. */}
          <ScopeToggle value={scope} onChange={onScopeChange} hasHomeScope={hasHomeScope} />
        </div>
      </div>

      <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0' }}>
        Searches active documents only.
        {mode === 'semantic'
          ? ' Answers are written from the documents you can already read, and quote them directly.'
          : ' Matches words in the title or the body.'}
      </p>
    </div>
  );
}

export default PolicySearchPanel;
